import React from "react";
import ReloadButton from "../../../common/components/shared/reloadButton/reloadButton";
import CollapsibleTable from "../../../common/components/shared/collapsibleTable/collapsibleTable";
import i18n from "../../../i18n/i18n";
import "./priceList.scss";
import PriceListRow from "./priceListRow/priceListRow";
import {PriceListModel} from "../../models/priceList";
import {PriceListService} from "../../services/priceListService";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../common/models/httpError";
import {Api} from "../../../helpers/api";
import {HttpError} from "../../../common/components/shared/httpError/httpError";
import Loader from "../../../common/components/shared/loader/loader";
import Date from "../../../timetables/components/actionForm/dateAndTime/date";
import {ValidatedValue} from "../../../helpers/validatedValue";
import SearchButton from "../../../common/components/shared/searchButton/searchButton";
import {DateUtils} from "../../../helpers/dateUtils";
import Validator from "../../../helpers/validator";
import {NavLink} from "react-router-dom";
import Button from "../../../common/components/shared/button/button";
import {routingAddresses} from "../../../helpers/routing";

interface PriceListState {
    priceList: PriceListModel[] | undefined,
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    startDate: ValidatedValue<Date | null>;
    isWaiting: boolean;
    startDateFilter?: string;
}

export default class PriceList extends React.Component <any, PriceListState> {
    constructor(props: any) {
        super(props);

        this.state = {
            priceList: undefined,
            response: undefined,
            startDate: new ValidatedValue(null),
            isWaiting: false,
            startDateFilter: undefined
        };
    }

    get priceListData () {
        return this.state.priceList;
    }

    get isCorrectStartDate() {
        const startDate = this.state.startDate.value;

        return startDate && startDate.getFullYear() &&
            Validator.isDateIsNumber(startDate) && startDate.getFullYear().toString().length === 4;
    }

    public async componentDidMount() {
        await this.getAllPriceList();
    }

    public render(): JSX.Element {
        return (
            <div className="price-list">
                {this.renderContent()}
            </div>
        );
    }

    private renderContent(): JSX.Element {
        const properties = ["priceListType", "startDate"];
        const subProperties = ["parkingTime", "parkingFee"];
        const headers = properties.map(key => i18n.t("priceList.headers." + key));
        const subHeaders = subProperties.map(key => i18n.t("priceList.headers." + key));
        const actions = this.renderActions;

        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={i18n.t("priceList.retrievalErrorSuffix")}
                />
            );
        } else {
            if (this.priceListData === undefined) {
                return <Loader/>;
            } else {
                return (
                    <>
                        {this.renderHeader()}
                        <div className="price-list-content">
                            {this.priceListData && this.priceListData!.length && this.priceListData!.length > 0 ? (
                                <CollapsibleTable
                                    headers={headers}
                                    subHeaders={subHeaders}
                                    properties={properties}
                                    subProperties={subProperties}
                                    rows={this.priceListData.map((row, index) => (
                                        <PriceListRow
                                            properties={properties}
                                            row={row}
                                            subHeaders={subHeaders}
                                            key={index}
                                            testId={`price-list-row-${index}`}
                                            actions={actions ? actions.bind(this) : actions}
                                        />
                                    ))}
                                    testId="price-list-table"
                                />
                            ) : (
                                <CollapsibleTable
                                    headers={headers}
                                    subHeaders={subHeaders}
                                    properties={properties}
                                    subProperties={subProperties}
                                    rows={[]}
                                    testId="price-list-table"
                                />
                            )}
                        </div>
                    </>
                );
            }
        }
    }

    private renderHeader() {
        return (
            <div className="header">
                <div className="filters-content">
                    {this.renderFilters()}
                </div>
                <div className="actions">
                    <SearchButton
                        isWaiting={this.state.isWaiting}
                        onClick={async () => {
                            this.setState({isWaiting: true});
                            await this.filterByStartDate();
                            this.setState({isWaiting: false});
                        }}
                    />
                    <NavLink to={routingAddresses.priceList.add} data-testid="add-price">
                        <Button type="primary" text={i18n.t("priceList.actions.addPrice")}/>
                    </NavLink>
                    <ReloadButton
                        reloadFunc={this.reload.bind(this)}
                        testId="price-list-reload-btn"
                    />
                </div>
            </div>
        );
    }

    private renderFilters() {
        return <div className="filters">
            <Date
                testId="filter-start-date"
                dateLabel={i18n.t("priceList.headers.startDate")}
                dateValue={this.state.startDate}
            />
        </div>;
    }

    private renderActions(element: PriceListModel): JSX.Element {
        // @ts-ignore
        const elementId = element.id;

        return (
            <>
                {elementId && DateUtils.isDateGreaterThanToday(element.startDate) && (
                    <>
                        <NavLink
                            to={`${routingAddresses.priceList.modify}/${elementId}`}
                            className="action-button"
                            data-testid={`modify-${elementId}`}
                        >
                            {i18n.t("priceList.actions.modifyPrice")}
                        </NavLink>
                    </>
                )}
            </>
        );
    }

    private async filterByStartDate() {
        if (!this.state.startDate.value) {
            await this.setState({startDateFilter: undefined});
            await this.getAllPriceList();
        }

        if (this.isCorrectStartDate) {
            const startDate = DateUtils.getDateWithoutTimeToUtc(this.state.startDate.value!);

            await this.setState({startDateFilter: `startDate<=${startDate}`});
            await this.getAllPriceList();
        }
    }

    private async getAllPriceList() {
        this.setState({priceList: undefined});

        const filters = {
            startDate: this.state.startDateFilter ? this.state.startDateFilter : ""
        };

        const response = await PriceListService.getAll(filters);

        this.setState({
            priceList: response?.data?.data || [],
            response
        });
    }

    private async reload() {
        this.setState({priceList: undefined});

        await this.getAllPriceList();
    }
}

